import {type ChangeSpec, EditorSelection} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'

import {getNewlinePrefix, getNewlineSuffix} from '../../../language-tools/markdown/block-spacing-utils'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {WRAP_DELIMITER_MAP, type WrappableDelimiter} from './constants'
import {wrapSelectionWithDelimiter} from './utils'

export type WrapWithDelimiterOptions = {
  /**
   * When true, insert a blank line before and after a newly created code block fence.
   * Default is false to preserve existing behavior.
   */
  addSurroundingNewlines?: boolean
}

export const wrapWithDelimiter =
  (delimiter: WrappableDelimiter): Command =>
  view => {
    if (view.state.selection.main.empty) return false

    if (delimiter === '`' || delimiter === '~') {
      return wrapSelectionWithFence(view, delimiter)
    }

    const suffix = WRAP_DELIMITER_MAP[delimiter]
    wrapSelectionWithDelimiter(view, delimiter, suffix)

    return true
  }

const wrapSelectionWithFence = (view: EditorView, fenceChar: '`' | '~'): boolean => {
  const {doc, selection} = view.state
  const range = selection.main

  const doubleFenceLength = 2
  const doubleFence = fenceChar.repeat(doubleFenceLength)
  const prefixStart = Math.max(0, range.from - doubleFenceLength)
  const prefixSample = doc.sliceString(prefixStart, range.from)
  const suffixSample = doc.sliceString(range.to, range.to + doubleFenceLength)
  const hasDoublePrefix = range.from >= doubleFenceLength && prefixSample === doubleFence
  const hasDoubleSuffix = suffixSample === doubleFence

  if (hasDoublePrefix && hasDoubleSuffix) {
    const selectionText = doc.sliceString(range.from, range.to)

    const suffixEnd = range.to + doubleFenceLength
    const docString = doc.toString()
    const root = getMarkdownTree(view.state)
    const leadingNewlines = getNewlinePrefix(root, docString, prefixStart)
    const trailingNewlines = getNewlineSuffix(docString, suffixEnd)

    const prefix = `${fenceChar}\n`
    const suffix = selectionText.endsWith('\n') ? fenceChar : `\n${fenceChar}`

    const changes: ChangeSpec[] = []
    if (leadingNewlines) changes.push({from: prefixStart, insert: leadingNewlines})
    changes.push({from: range.from, insert: prefix})
    changes.push({from: range.to, insert: suffix})
    if (trailingNewlines) changes.push({from: suffixEnd, insert: trailingNewlines})

    const offsetBeforeSelection = leadingNewlines.length + prefix.length

    view.dispatch(
      view.state.changeByRange(r => ({
        changes,
        range: EditorSelection.range(r.from + offsetBeforeSelection, r.to + offsetBeforeSelection),
      })),
      {userEvent: 'input', scrollIntoView: true},
    )

    return true
  }

  wrapSelectionWithDelimiter(view, fenceChar, fenceChar)
  return true
}
